import { useState } from 'react';
import { useTags, useCreateTag, slugify } from '@wordpuppi/shared';
import { LoadingSpinner } from './LoadingSkeleton';
import { toast } from 'sonner';

interface TagSelectorProps {
  siteSlug: string;
  selectedTagIds: string[];
  onChange: (tagIds: string[]) => void;
}

export function TagSelector({ siteSlug, selectedTagIds, onChange }: TagSelectorProps) {
  const { data: tagsData, isLoading } = useTags(siteSlug);
  const createTag = useCreateTag(siteSlug);
  const [newTagName, setNewTagName] = useState('');

  const tags = tagsData?.data ?? [];

  const handleToggleTag = (tagId: string) => {
    if (selectedTagIds.includes(tagId)) {
      onChange(selectedTagIds.filter((id) => id !== tagId));
    } else {
      onChange([...selectedTagIds, tagId]);
    }
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;

    try {
      const result = await createTag.mutateAsync({
        name: newTagName.trim(),
        slug: slugify(newTagName.trim()),
      });
      // Add the new tag to selection
      onChange([...selectedTagIds, result.data!.id]);
      setNewTagName('');
      toast.success('Tag created');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create tag');
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="tag-selector">
      <div className="tag-row">
        <label className="form-label">Tags</label>
        <div className="tag-list">
        {tags.length === 0 ? (
          <span className="tag-empty">No tags yet</span>
        ) : (
          tags.map((tag) => (
            <button
              key={tag.id}
              type="button"
              onClick={() => handleToggleTag(tag.id)}
              className={`tag-chip ${selectedTagIds.includes(tag.id) ? 'selected' : ''}`}
            >
              {tag.name}
            </button>
          ))
        )}
        </div>
      </div>
      <div className="tag-create-form">
        <input
          type="text"
          value={newTagName}
          onChange={(e) => setNewTagName(e.target.value)}
          placeholder="Add new tag..."
          className="form-control tag-input"
        />
        <button
          type="button"
          onClick={handleCreateTag}
          className="btn btn-secondary btn-sm"
          disabled={!newTagName.trim() || createTag.isPending}
        >
          {createTag.isPending ? '...' : 'Add'}
        </button>
      </div>
      <style>{`
        .tag-selector {
          margin-top: 0.5rem;
        }
        .tag-row {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 0.5rem;
        }
        .form-label {
          font-weight: 500;
          margin: 0;
        }
        .tag-list {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }
        .tag-empty {
          color: var(--color-text-muted);
          font-size: 0.875rem;
        }
        .tag-chip {
          padding: 0.25rem 0.75rem;
          border-radius: 9999px;
          font-size: 0.875rem;
          border: 1px solid var(--color-border);
          background: var(--color-surface);
          color: var(--color-text);
          cursor: pointer;
          transition: border-color 0.15s, background-color 0.15s, color 0.15s;
        }
        .tag-chip:hover {
          border-color: var(--color-primary);
        }
        .tag-chip.selected {
          background: var(--color-foreground);
          border-color: var(--color-foreground);
          color: var(--color-background);
        }
        .tag-create-form {
          display: flex;
          gap: 0.5rem;
        }
        .tag-input {
          flex: 1;
          max-width: 200px;
          font-size: 0.875rem;
        }
        .btn-sm {
          padding: 0.25rem 0.75rem;
          font-size: 0.875rem;
        }
      `}</style>
    </div>
  );
}
